import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Save, Search, Sparkles, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_PAGES = [
  { key: 'home', name: 'Home', defaultTitle: 'Quality Used Cars | We Have Your Car', defaultMeta: 'Find affordable, quality used cars in Austin, TX. We Have Your Car offers flexible financing for all credit situations. Browse inventory, apply online, drive today.', defaultH1: 'Quality Used Cars in Austin, Texas', defaultH2: 'Affordable Vehicles with Flexible Financing for All Credit Situations' },
  { key: 'inventory', name: 'Inventory', defaultTitle: 'Inventory | We Have Your Car', defaultMeta: 'Browse our selection of inspected used cars, SUVs, and minivans in Austin, TX. Prices from $4,250. Filter by make, price, body type, and features. Updated daily.', defaultH1: 'Browse Our Inventory', defaultH2: 'Quality Used Vehicles Ready to Drive Home Today' },
  { key: 'about', name: 'About Us', defaultTitle: 'About Us | We Have Your Car', defaultMeta: "Learn about We Have Your Car, Austin's trusted used car dealership. 5 years serving 500+ happy customers with transparent pricing and all-credit financing.", defaultH1: 'About We Have Your Car', defaultH2: "Austin's Trusted Source for Affordable Used Vehicles" },
  { key: 'contact', name: 'Contact Us', defaultTitle: 'Contact Us | We Have Your Car', defaultMeta: 'Contact We Have Your Car at (512) 666-0016. Visit us at 8203 Bluff Springs Rd, Austin, TX 78744. Mon-Sat 10AM-6PM.', defaultH1: 'Contact Us', defaultH2: "We're Here to Help You Find the Right Car" },
  { key: 'credit-application', name: 'Apply for Financing', defaultTitle: 'Apply for Financing | We Have Your Car', defaultMeta: 'Apply for auto financing in Austin, TX. Bad credit, no credit, bankruptcy welcome. Fast approval in minutes.', defaultH1: 'Apply for Financing', defaultH2: 'Get Pre-Approved in Minutes — All Credit Situations Welcome' },
  { key: 'faq', name: 'FAQ', defaultTitle: 'Frequently Asked Questions | We Have Your Car', defaultMeta: 'Find answers about We Have Your Car: financing, inventory, trade-ins, visiting our Austin dealership, and the buying process.', defaultH1: 'Frequently Asked Questions', defaultH2: 'Everything You Need to Know About We Have Your Car' },
  { key: 'discovery-faqs', name: 'Discovery FAQs', defaultTitle: 'Car Buying in Austin, Texas - Discovery FAQs | We Have Your Car', defaultMeta: 'Answers to common car buying questions in Austin, TX. Credit scores, down payments, financing, and finding affordable used cars.', defaultH1: 'Car Buying in Austin, Texas', defaultH2: 'Questions People Ask Before Finding a Dealership' },
  { key: 'referral-program', name: 'Referral Program', defaultTitle: 'Refer & Earn $100 | We Have Your Car', defaultMeta: 'Earn $100 for every friend you refer to We Have Your Car in Austin, TX. No limit on referrals.', defaultH1: 'Referral Program', defaultH2: 'Earn $100 Cash for Every Friend You Refer' },
  { key: 'privacy-policy', name: 'Privacy Policy', defaultTitle: 'Privacy Policy | We Have Your Car', defaultMeta: "Read We Have Your Car's privacy policy. How we collect, use, and protect your personal information.", defaultH1: 'Privacy Policy', defaultH2: 'How We Protect Your Personal Information' },
  { key: 'sms-terms', name: 'Terms of Service', defaultTitle: 'Terms of Service | We Have Your Car', defaultMeta: "Review We Have Your Car's terms of service and SMS messaging terms.", defaultH1: 'Terms of Service', defaultH2: 'Our Policies and Your Rights' },
  { key: 'blog', name: 'Blog', defaultTitle: 'Blog | We Have Your Car', defaultMeta: 'Car buying tips, automotive advice, and dealership news from We Have Your Car in Austin, TX.', defaultH1: 'Blog', defaultH2: 'Car Buying Tips and Dealership News' },
];

const SEOManagement = () => {
  const [seoData, setSeoData] = useState({});
  const [saving, setSaving] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [expandedPage, setExpandedPage] = useState(null);

  useEffect(() => {
    fetchSEO();
  }, []);

  const fetchSEO = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/api/admin/seo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const seoObj = {};
      response.data.forEach(item => {
        seoObj[item.page_key] = item;
      });
      // Fill in defaults for pages without SEO data
      DEFAULT_PAGES.forEach(page => {
        if (!seoObj[page.key]) {
          seoObj[page.key] = {
            page_key: page.key,
            title: page.defaultTitle,
            meta_description: page.defaultMeta || '',
            h1: page.defaultH1 || '',
            h2: page.defaultH2 || ''
          };
        } else {
          // Ensure h1/h2 fields exist
          if (!seoObj[page.key].h1) seoObj[page.key].h1 = page.defaultH1 || '';
          if (!seoObj[page.key].h2) seoObj[page.key].h2 = page.defaultH2 || '';
        }
      });
      setSeoData(seoObj);
    } catch (error) {
      console.error('Error fetching SEO:', error);
    }
  };

  const updateSEOField = (pageKey, field, value) => {
    setSeoData(prev => ({
      ...prev,
      [pageKey]: { ...prev[pageKey], [field]: value }
    }));
  };

  const saveSEO = async (pageKey) => {
    setSaving(pageKey);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/api/admin/seo/${pageKey}`, seoData[pageKey], {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SEO updated successfully');
    } catch (error) {
      toast.error('Failed to update SEO');
    }
    setSaving(null);
  };

  const generateWithAI = async (pageKey) => {
    const page = DEFAULT_PAGES.find(p => p.key === pageKey);
    if (!page) return;
    
    setGenerating(pageKey);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/api/admin/generate-page-seo`, {
        page_key: pageKey,
        page_name: page.name,
        current_title: seoData[pageKey]?.title || page.defaultTitle
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSeoData(prev => ({
          ...prev,
          [pageKey]: {
            ...prev[pageKey],
            title: response.data.title || prev[pageKey]?.title,
            meta_description: response.data.meta_description || prev[pageKey]?.meta_description,
            h1: response.data.h1 || prev[pageKey]?.h1,
            h2: response.data.h2 || prev[pageKey]?.h2,
          }
        }));
        toast.success('AI-generated SEO content applied! Click Save to keep changes.');
      }
    } catch (error) {
      toast.error('Failed to generate SEO with AI');
    }
    setGenerating(null);
  };

  const getTitleColor = (title) => {
    if (!title) return 'text-red-400';
    if (title.length > 60) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getDescColor = (desc) => {
    if (!desc) return 'text-red-400';
    if (desc.length > 160) return 'text-yellow-400';
    return 'text-green-400';
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-2">SEO Management</h1>
      <p className="text-[#a8b8d0] text-sm mb-6">Manage page titles, meta descriptions, H1 headings, and H2 subheadings for all pages.</p>
      
      <div className="space-y-3">
        {DEFAULT_PAGES.map(page => {
          const data = seoData[page.key] || {};
          const isExpanded = expandedPage === page.key;
          
          return (
            <div key={page.key} className="bg-[#132238] rounded-xl border border-[#1a2d47] overflow-hidden" data-testid={`seo-page-${page.key}`}>
              {/* Header Row */}
              <button 
                onClick={() => setExpandedPage(isExpanded ? null : page.key)}
                className="w-full flex items-center justify-between p-4 hover:bg-[#1a2d47]/30 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <Globe size={16} className="text-[#1a4d8c]" />
                  <span className="text-white font-medium">{page.name}</span>
                  <span className="text-[#64748b] text-xs">/{page.key === 'home' ? '' : page.key}</span>
                </div>
                <div className="flex items-center gap-2">
                  {data.meta_description ? (
                    <span className="text-green-400 text-xs bg-green-500/10 px-2 py-0.5 rounded-full">SEO Set</span>
                  ) : (
                    <span className="text-red-400 text-xs bg-red-500/10 px-2 py-0.5 rounded-full">Missing</span>
                  )}
                  <span className="text-[#64748b]">{isExpanded ? '−' : '+'}</span>
                </div>
              </button>

              {/* Expanded Fields */}
              {isExpanded && (
                <div className="px-4 pb-4 space-y-4 border-t border-[#1a2d47]">
                  <div className="flex justify-end pt-3">
                    <button
                      onClick={() => generateWithAI(page.key)}
                      disabled={generating === page.key}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600/20 text-purple-300 hover:bg-purple-600/30 rounded-lg text-sm transition-colors disabled:opacity-50"
                      data-testid={`seo-generate-ai-${page.key}`}
                    >
                      {generating === page.key ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      {generating === page.key ? 'Generating...' : 'Generate with AI'}
                    </button>
                  </div>

                  {/* Title */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[#a8b8d0] text-sm">Page Title</label>
                      <span className={`text-xs ${getTitleColor(data.title)}`}>
                        {(data.title || '').length}/60
                      </span>
                    </div>
                    <input
                      type="text"
                      value={data.title || ''}
                      onChange={(e) => updateSEOField(page.key, 'title', e.target.value)}
                      className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Page title..."
                    />
                  </div>

                  {/* Meta Description */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[#a8b8d0] text-sm">Meta Description</label>
                      <span className={`text-xs ${getDescColor(data.meta_description)}`}>
                        {(data.meta_description || '').length}/160
                      </span>
                    </div>
                    <textarea
                      value={data.meta_description || ''}
                      onChange={(e) => updateSEOField(page.key, 'meta_description', e.target.value)}
                      className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
                      placeholder="Meta description..."
                    />
                  </div>

                  {/* H1 Heading */}
                  <div>
                    <label className="text-[#a8b8d0] text-sm mb-1 block">H1 Heading</label>
                    <input
                      type="text"
                      value={data.h1 || ''}
                      onChange={(e) => updateSEOField(page.key, 'h1', e.target.value)}
                      className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Main heading..."
                    />
                  </div>

                  {/* H2 Subheading */}
                  <div>
                    <label className="text-[#a8b8d0] text-sm mb-1 block">H2 Subheading</label>
                    <input
                      type="text"
                      value={data.h2 || ''}
                      onChange={(e) => updateSEOField(page.key, 'h2', e.target.value)}
                      className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="Subheading..."
                    />
                  </div>

                  {/* Search Preview */}
                  <div className="bg-[#0a1628] rounded-lg p-3 border border-[#1a2d47]">
                    <p className="text-xs text-[#64748b] mb-1">Google Search Preview</p>
                    <p className="text-blue-400 text-sm truncate">{data.title || 'Page Title'}</p>
                    <p className="text-green-500 text-xs truncate">wehaveyourcar.com/{page.key === 'home' ? '' : page.key}</p>
                    <p className="text-[#a8b8d0] text-xs mt-1 line-clamp-2">{data.meta_description || 'No description set'}</p>
                  </div>

                  {/* Save Button */}
                  <button
                    onClick={() => saveSEO(page.key)}
                    disabled={saving === page.key}
                    className="flex items-center gap-2 px-4 py-2 bg-[#ff6b35] hover:bg-[#e55a2b] text-white rounded-lg text-sm transition-colors disabled:opacity-50"
                    data-testid={`seo-save-${page.key}`}
                  >
                    <Save size={14} />
                    {saving === page.key ? 'Saving...' : 'Save SEO'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SEOManagement;
